// Learner analytics derivations (§5.2.5): turns the gamification daily-activity
// log into velocity / tutor-usage time-series, and produces prescriptive
// "recommended next actions". Pure functions — no IO.

import type { DailyActivityRecord } from '@shared/types/gamification';
import type { LearningPath } from '@shared/types/learning-path';
import type { ContentDocument } from '@shared/types/content';
import type { ScoredConcept } from './mastery';

export interface SeriesPoint {
  date: string; // YYYY-MM-DD
  value: number;
}

// Last `days` local date keys, oldest → newest.
function recentDayKeys(days: number): string[] {
  const keys: string[] = [];
  const now = new Date();
  for (let i = days - 1; i >= 0; i -= 1) {
    const d = new Date(now);
    d.setDate(now.getDate() - i);
    keys.push(
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
    );
  }
  return keys;
}

function byDate(daily: DailyActivityRecord[]): Map<string, DailyActivityRecord> {
  return new Map(daily.map((d) => [d.date, d]));
}

function sumActions(rec: DailyActivityRecord | undefined, keys: string[]): number {
  if (!rec) return 0;
  return keys.reduce((sum, k) => sum + (rec.actionCounts?.[k] ?? 0), 0);
}

// Concepts mastered momentum: checkpoints passed + nodes completed per day.
export function velocitySeries(daily: DailyActivityRecord[], days = 30): SeriesPoint[] {
  const map = byDate(daily);
  return recentDayKeys(days).map((date) => ({
    date,
    value: sumActions(map.get(date), ['node_completed', 'checkpoint_passed', 'checkpoint_perfect']),
  }));
}

// Tutor queries per day.
export function tutorSeries(daily: DailyActivityRecord[], days = 30): SeriesPoint[] {
  const map = byDate(daily);
  return recentDayKeys(days).map((date) => ({
    date,
    value: sumActions(map.get(date), ['tutor_query']),
  }));
}

const TUTOR_MODES: Array<{ key: string; label: string }> = [
  { key: 'tutor_mode_explain', label: 'Explain' },
  { key: 'tutor_mode_go_deeper', label: 'Go deeper' },
  { key: 'tutor_mode_test_me', label: 'Test me' },
  { key: 'tutor_mode_connect', label: 'Connect' },
  { key: 'tutor_mode_simplify', label: 'Simplify' },
];

export interface ModeSlice {
  label: string;
  count: number;
}

export function tutorModeDistribution(daily: DailyActivityRecord[]): ModeSlice[] {
  return TUTOR_MODES.map(({ key, label }) => ({
    label,
    count: daily.reduce((sum, d) => sum + (d.actionCounts?.[key] ?? 0), 0),
  })).filter((m) => m.count > 0);
}

export interface Recommendation {
  id: string;
  title: string;
  detail: string;
  href: string;
  tone: 'focus' | 'continue' | 'tutor' | 'streak';
}

// Resolve the content (id + title) that teaches a given concept.
function contentForConcept(
  conceptId: string,
  paths: LearningPath[],
  content: ContentDocument[],
): { contentId: string; title: string } | null {
  for (const p of paths) {
    if ((p.nodes ?? []).some((n) => (n.conceptIds ?? []).includes(conceptId))) {
      const doc = content.find((c) => c.contentId === p.contentId);
      return { contentId: p.contentId, title: doc?.title ?? 'your material' };
    }
  }
  return null;
}

export function buildRecommendations(input: {
  concepts: ScoredConcept[];
  paths: LearningPath[];
  content: ContentDocument[];
  daily: DailyActivityRecord[];
}): Recommendation[] {
  const { concepts, paths, content, daily } = input;
  const recs: Recommendation[] = [];

  // 1. Weakest concept that needs work → revisit it.
  const weakest = [...concepts].sort((a, b) => a.score - b.score)[0];
  if (weakest && weakest.score < 70) {
    const c = contentForConcept(weakest.conceptId, paths, content);
    if (c) {
      recs.push({
        id: 'focus-weakest',
        title: `Revisit ${weakest.name}`,
        detail: `Your lowest mastery so far (${Math.round(weakest.score)}%). A focused pass will lift it fastest.`,
        href: `/learn/${c.contentId}`,
        tone: 'focus',
      });
    }
  }

  // 2. An in-progress path → continue it.
  const inProgress = paths.find((p) => {
    const total = p.nodes?.length ?? 0;
    const done = p.progress?.completedNodes?.length ?? 0;
    return p.generationStatus === 'complete' && total > 0 && done < total;
  });
  if (inProgress) {
    const doc = content.find((c) => c.contentId === inProgress.contentId);
    const total = inProgress.nodes?.length ?? 0;
    const done = inProgress.progress?.completedNodes?.length ?? 0;
    recs.push({
      id: 'continue-path',
      title: `Continue ${doc?.title ?? 'your path'}`,
      detail: `${done} of ${total} steps done — pick up where you left off.`,
      href: `/learn/${inProgress.contentId}`,
      tone: 'continue',
    });
  }

  // 3. Tutor under-use while a weak concept exists → nudge to ask.
  const tutorLast7 = tutorSeries(daily, 7).reduce((s, p) => s + p.value, 0);
  if (tutorLast7 === 0 && weakest && weakest.score < 85) {
    const c = contentForConcept(weakest.conceptId, paths, content);
    if (c) {
      recs.push({
        id: 'use-tutor',
        title: `Ask Professor Cogni about ${weakest.name}`,
        detail: 'You haven’t used the tutor this week — a quick question often unblocks a weak spot.',
        href: `/learn/${c.contentId}?tutor=open`,
        tone: 'tutor',
      });
    }
  }

  // 4. Inactivity → momentum nudge.
  const activeLast3 = velocitySeries(daily, 3).reduce((s, p) => s + p.value, 0)
    + tutorSeries(daily, 3).reduce((s, p) => s + p.value, 0);
  if (activeLast3 === 0) {
    recs.push({
      id: 'momentum',
      title: 'Keep your momentum',
      detail: 'A five-minute review of your weakest concepts keeps everything fresh.',
      href: '/dashboard',
      tone: 'streak',
    });
  }

  return recs.slice(0, 4);
}
